import { Input, Space, Spin, Table } from 'antd';
import crypto from 'crypto';
import Decimal from 'decimal.js';
import React, { useEffect, useState, useRef } from 'react';
import { hideModal } from '../../../../redux/reducers/ModalReducer';
import { addMatchWorkToUsage, searchDataChanged, updateAdjustmentField } from '../../../../redux/reducers/RepertoireReducer';
import { IAdjustmentDetails } from '../../../../redux/types/IAdjustmentDetails';
import { IAdjustmentState, IAdjustmentStateKeys } from '../../../../redux/types/IAdjustmentState';
import IRepertoireComponentDataItem from '../../../../redux/types/IRepertoireComponentDataItem';
import ActionButton, { SizedActionButton } from '../../../components/actionButton/ActionButton';
import IconTextButton from '../../../components/iconTextButton/IconTextButton';
import ShowMore from '../../../components/showMore/ShowMore';
import InputWithKebabMenu from '../../../components/inputWithKebabMenu/InputWithKebabMenu';
import {
    ALLOCATION_ACCOUNT_NUMBER,
    ALLOCATION_ADJUSTMENT_IP_KEY,
    ALLOCATION_AMOUNT_ADJUSTED,
    ALLOCATION_DISTRIBUTION,
    ALLOCATION_HEADER_ID,
    ALLOCATION_IPI_NUMBER,
    ALLOCATION_IP_NAME,
    ALLOCATION_CONTRIBUTOR_ROLE,
    ALLOCATION_POINT_ADJUSTED,
    ALLOCATION_POOL,
    ALLOCATION_SEGMENT,
    ALLOCATION_SHARE_ADJUSTED,
    ALLOCATION_SOCIETY_AFFILIATION,
    ALLOCATION_WORK_NAME,
    ALLOCATION_WORK_NUMBER,
    REMOVE_ACTION,
    REPERTOIRE,
    SEARCH_VIEW,
    USAGE_MATCHING_WORKS_SECTION_KEY,
    WORK_MAINTENANCE_TOOLBAR,
    ALLOCATION_USAGE_GROUP_TITLE
} from '../../../Consts';
import { AllocationHeaderRowType } from "../../../types/usageTypes/IAllocationRowTypes";
import UsageSearchView from '../../../components/usageComponents/searchView/UsageSearchView';
import { IUsagesSearchQuery } from '../../../types/usageTypes/IUsagesSearchQuery';
import { ITreeData } from '../../../types/ITreeData';
import { IWorksSearchResult } from '../../../types/IWorksSearchResult';
import { IWorksSearchQuery } from '../../../types/IWorksSearchQuery';
import { IMatchWorksSearchQuery } from '../../../types/usageTypes/IMatchWorksSearchQuery';
import { IMatchProductsSearchQuery } from '../../../types/usageTypes/IMatchProductsSearchQuery';
import { IMatchWorksSearchResult } from '../../../types/usageTypes/IMatchWorksSearchResult';
import { IDistributionType } from '../../../types/usageTypes/IDistibutionType';
import { ILookupDictionary } from '../../../../lookup/types/ILookupDictionary';
import { IDataActionToolbar } from '../../../types/IDataActionToolbar';
import { FormatFields } from '../../../../redux/types/FormatFields';
import { IUsageGroupState } from '../../../../redux/types/IUsageGroupState';
import { IAdjustmentMaintenanceStateKeys } from '../../../../redux/types/IAdjustmentMaintenanceState';
import { IMatchingWorksRowData } from '../../../types/usageTypes/IMatchingWorksRowData';
import { SearchRequests } from '../../../services/SearchRequests';
import { CONFIGURATION_PARAMETER_FIELD_FORMAT_KEY, WORK_MAINTENANCE_GROUP } from '../../../ConfigurationConsts';
import { getDataAction } from '../../../components/toolBar/ToolbarHelper';
import { ITabReduxItem } from '../../../../redux/types/ITabReduxItem';
import { SCREEN_SIZE_BIG_ENOUGH_FOR_TWO_SCREENS } from '../../../usage/components/usageMaintenancePage/UsageMaintenancePage';
import { DataSource } from '../../../types/DataSource';
import { EXTERNAL_SVG } from '../../../IconConsts';
import { AGENCY_LOOKUP } from '../../../../lookup/Consts';
import { searchLookupValueByCode } from '../../../../lookup/services/LookupHelpers';
import { IAdjustmentDetailsToSocietyAffiliation, SocietyAffiliationRightType } from "../../../types/ISocietyAffiliation";
import {
    addToRowWithRemainingShare,
    adjustmentDetailsDerivedValuesChanged,
    isMatchingAdjustmentDetailFromRow,
    recalculateForFromTotalChange,
    redistributeToRowShares,
    sortAdjustmentDetailsByAllocationOrder,
    syncAdjustmentDetailDerivedValues
} from '../../../services/AdjustmentShareHelpers';
import { Delete20Regular } from '@fluentui/react-icons';

Decimal.set({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

const toDecimal = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') {
        return new Decimal(0);
    }

    try {
        return new Decimal(value);
    } catch {
        return new Decimal(0);
    }
};

const formatDecimal = (value: Decimal) => value.toFixed(18);

interface IAllocationAdjustmentDetailsViewProps {
    allocationAdjustmentDetailsViewData: IRepertoireComponentDataItem;
    adjustment: IAdjustmentState;
    updateAdjustmentField: (value: any, fieldName: IAdjustmentStateKeys) => void;
    showModal: (
        modalContent: string,
        modalComponentInstance: string,
        modalProps: any,
        displayModalCloseButton: boolean,
        title: string
    ) => void;
    allocationRecords: AllocationHeaderRowType[];
    searchUsages?: (query: IUsagesSearchQuery, modalOpen: boolean, usageType: string, lookupSources: ITreeData[], continuationToken?: string) => void;
    resetPagination?: (repertoireSection: string) => void;
    updatePagination?: (indexOfFirstResult: number, indexOfLastResult: number, resultsPerPage: number, currentPage: number, repertoireSection: string) => void;
    sortSearchResults?: (name: string, results: any) => void;
    searchWorks?: (query: IWorksSearchQuery, modalOpen: boolean) => void;
    worksSearchResults?: IWorksSearchResult[];
    expandedWorkResults?: number[];
    searchSuccessful?: boolean;
    expandAll?: boolean;
    indexOfFirstResult?: number;
    indexOfLastResult?: number;
    resultsPerPage?: number;
    expandWorkResult?: (workResult: number) => void;
    expandAllResults?: () => void;
    currentPage?: number;
    username?: string;
    searchMatchWorks?: (query: IMatchWorksSearchQuery, modalOpen: boolean) => void;
    searchMatchProducts?: (query: IMatchProductsSearchQuery, modalOpen: boolean) => void;
    matchWorkSearchResult?: IMatchWorksSearchResult[];
    expandedMatchWorkResults?: number[];
    distributionTypes?: IDistributionType[];
    sourceDistributionRightType?: SocietyAffiliationRightType;
    lookupSources?: ITreeData[];
    usageMatchingDefaultsWorks?: string[];
    addMatchworkToUsage?: typeof addMatchWorkToUsage;
    usageExpandAllResults?: () => void;
    expandMatchWorkResult?: (matchWorkResults: number) => void;
    paneIndex?: number;
    formatFields?: FormatFields[];
    updateUsagesAdvanced?: boolean;
    lookupValues?: ILookupDictionary;
    searchResultsTableData?: IRepertoireComponentDataItem;
    searchViewData?: IRepertoireComponentDataItem;
    getWorkDetails?: (dataSource: string, workID: number, lookups: ILookupDictionary, otherIndicatorsWorkFlagTypes: string[], readonlyIndicatorsWorkFlagTypes: string[], dataActions?: IDataActionToolbar[], workMaintenanceGeneralDataViewData?: IRepertoireComponentDataItem, formats?: FormatFields[], openEntityForWorflowSession?: boolean, isOpenedInSecondTab?: boolean) => void;
    isAllocationSelected?: boolean;
    isWorkAdjust?: boolean;
    updateAllocationRecordField: (value: any, fieldName: IAdjustmentMaintenanceStateKeys) => void;
    saveSuccessful?: boolean;
    workMaintenanceGeneralDataViewData?: IRepertoireComponentDataItem;
    activeTab: number;
    tabs: ITabReduxItem[];
    isReadonly?: boolean;
    enabledAdjustmentICEWorkSearch?: boolean;
}

interface AllocationAdjustmentDetailsRowType {
    key: React.Key;
    direction: string;
    allocationHeaderID?: number;
    adjustmentDetailID: number;
    [adjustmentDetailID: number]: number;
    distribution: string;
    pool: string;
    segment: string;
    workNumber: string;
    workName: string;
    usageGroupTitle?: string;
    accountNumber: string;
    role?: string;
    name: string;
    ipiNumber: number;
    societyAffiliation?: string;
    affiliationKey?: string;
    sharePctAdj: string;
    pointsAdj?: string;
    amountAdj: string;
    toRowIndex?: number;
}

interface IAllocationAdjustmentDetailsViewState {
    loaded: boolean;
    searchCriteriaVisible: boolean;
    workAdjustmentIndex: number;
    isWorkLevelAdjustment?: boolean;
    setMatchWorkSearchResult?: any
}

interface IToRowAffiliationState {
    value: string;
    loading: boolean;
    identity: string;
}

const AllocationAdjustmentDetailsView: React.FC<IAllocationAdjustmentDetailsViewProps> = ({ 
        allocationAdjustmentDetailsViewData,
        saveSuccessful,
        adjustment,
        showModal,
        updateAllocationRecordField,
        isWorkAdjust,
        workMaintenanceGeneralDataViewData,
        tabs,
        activeTab,
        searchUsages, 
        searchSuccessful, 
        indexOfFirstResult, 
        resultsPerPage,
        resetPagination,
        updatePagination,
        searchMatchWorks,
        searchMatchProducts,
        matchWorkSearchResult,
        expandMatchWorkResult,
        expandedMatchWorkResults,
        sourceDistributionRightType,
        lookupValues,
        lookupSources,
        distributionTypes,
        usageMatchingDefaultsWorks,
        usageExpandAllResults,
        searchResultsTableData,
        indexOfLastResult,
        currentPage,
        searchViewData,
        getWorkDetails,
        isReadonly,
        isAllocationSelected,
        updateAdjustmentField,
        enabledAdjustmentICEWorkSearch,
        allocationRecords
    }) => {
    const { fields } = allocationAdjustmentDetailsViewData;

    const [loaded, setLoaded] = useState(false);
    const [workAdjustmentIndex, setWorkAdjustmentIndex] = useState<number | undefined>(undefined);
    const [isWorkLevelAdjustment, setIsWorkLevelAdjustment] = useState(false);
    const [viewData, setViewData] = useState<any>({});
    const [toRowAffiliations, setToRowAffiliations] = useState<Record<string, IToRowAffiliationState>>({});
    const [initialAffiliationsLoading, setInitialAffiliationsLoading] = useState(true);
    const initialAffiliationsLoadedRef = useRef(false);
    // Holds the in-progress text of a Share% input while it is focused, so typing doesn't
    // get clobbered by the full-precision (18 decimal place) value on every keystroke.
    const [shareEditBuffer, setShareEditBuffer] = useState<Record<string, string>>({});

    const getSocietyAffiliation = (societyCode?: string) => societyCode ? searchLookupValueByCode(AGENCY_LOOKUP, lookupValues, societyCode) || societyCode : '';

    // Identifies the table row
    const getToRowKey = (adjustmentDetailsId: number, toRowIndex: number) => `${adjustmentDetailsId}_${toRowIndex}`;

    // Identifies a Share% input while it's being edited
    const getShareEditKey = (adjustmentIndex: number, toRowIndex: number) => `${adjustmentIndex}_${toRowIndex}`;

    // Identifies the currently selected IP
    const getToRowIdentity = (ipiNumber?: number, accountNumber?: string) => `${ipiNumber ?? ""}_${accountNumber ?? ""}`;

    const resolveToRowAffiliation = async (adjustmentDetailsId: number, toRowIndex: number, ipiNumber?: number, accountNumber?: string, shareSet?: string) => {
        if ((!ipiNumber && !accountNumber) || !sourceDistributionRightType) {
            return;
        }

        const rowKey = getToRowKey(
            adjustmentDetailsId,
            toRowIndex
        );

        const identity = getToRowIdentity(
            ipiNumber,
            accountNumber
        );

        setToRowAffiliations(current => ({
            ...current,
            [rowKey]: {
                value: "",
                loading: true,
                identity
            }
        }));

        try {
            const result = await SearchRequests.getSocietyAffiliation({ipiNumber, accountNumber, rightType: sourceDistributionRightType, shareSet});

            setToRowAffiliations(current => {
                if (current[rowKey]?.identity !== identity) {
                    return current;
                }

                return {
                    ...current,
                    [rowKey]: {
                        value:
                            result.societyAffiliation ?? "",
                        loading: false,
                        identity
                    }
                };
            });
        } catch {
            setToRowAffiliations(current => {
                if (current[rowKey]?.identity !== identity) {
                    return current;
                }

                return {
                    ...current,
                    [rowKey]: {
                        value: "",
                        loading: false,
                        identity
                    }
                };
            });
        }
    };

    useEffect(() => {
        if (!sourceDistributionRightType) {
            initialAffiliationsLoadedRef.current = true;
            setInitialAffiliationsLoading(false);
            setToRowAffiliations({});
            return;
        }

        initialAffiliationsLoadedRef.current = false;
        setInitialAffiliationsLoading(true);
        setToRowAffiliations({});

        const loadInitialAffiliations = async () => {
            const requests = adjustment.adjustmentDetails
                ?.flatMap(detail =>
                    detail.adjustmentDetailTo
                        ?.map((row, toRowIndex) =>
                            row.ipNumber || row.accountNumber
                                ? resolveToRowAffiliation(
                                    detail.id,
                                    toRowIndex,
                                    row.ipNumber,
                                    row.accountNumber
                                )
                                : null
                        )
                        .filter(Boolean)
                ) ?? [];

            try {
                await Promise.all(requests);
            } finally {
                initialAffiliationsLoadedRef.current = true;
                setInitialAffiliationsLoading(false);
            }
        };

        loadInitialAffiliations();
    }, [adjustment.id, sourceDistributionRightType]);

    useEffect(() => {
        if (fields) {
            const visibleFields = fields.filter(field => !field.hidden);
            const tempViewData = {};

            visibleFields.forEach(item => {
                tempViewData[item.name] = item.data;
            });

            if (Object.keys(tempViewData).length === visibleFields.length) {
                setViewData(tempViewData);
                setLoaded(true);
            }
        }
    }, [fields]);

    useEffect(() => {
        const { adjustmentDetails } = adjustment;

        let indexToUpdate = adjustment.workSearchIndex ?? workAdjustmentIndex;

        if (indexToUpdate !== undefined && indexToUpdate !== null) {
            let specificId = adjustmentDetails[indexToUpdate]?.id;

            if (specificId) {
                const updatedArr = adjustmentDetails.map(item =>
                    item.id === specificId
                        ? { ...item, workSearchResult: matchWorkSearchResult }
                        : item
                );

                updateAdjustmentField(updatedArr, "adjustmentDetails");
            }
        }
    }, [matchWorkSearchResult]);

    useEffect(() => {
        setWorkAdjustmentIndex(undefined);
    }, [saveSuccessful]);

    const fromSharesSignature = adjustment.adjustmentDetails
        ?.map(detail => detail.adjustmentDetailFrom.map(from => from.sharePercentAdjusted).join(','))
        .join('|');
    const previousFromSharesSignatureRef = useRef<string | undefined>(undefined);
    const previousAdjustmentIdRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!adjustment.adjustmentDetails?.length) {
            return;
        }

        if (previousAdjustmentIdRef.current !== adjustment.id) {
            previousAdjustmentIdRef.current = adjustment.id;
            previousFromSharesSignatureRef.current = fromSharesSignature;
            return;
        }

        if (previousFromSharesSignatureRef.current === undefined) {
            previousFromSharesSignatureRef.current = fromSharesSignature;
            return;
        }

        if (previousFromSharesSignatureRef.current === fromSharesSignature) {
            return;
        }

        previousFromSharesSignatureRef.current = fromSharesSignature;
        const syncedDetails = adjustment.adjustmentDetails.map(recalculateForFromTotalChange);

        if (adjustmentDetailsDerivedValuesChanged(adjustment.adjustmentDetails, syncedDetails)) {
            updateAdjustmentField(syncedDetails, 'adjustmentDetails');
        }
    }, [fromSharesSignature, adjustment.id]);

    useEffect(() => {
        if (!initialAffiliationsLoadedRef.current) {
            return;
        }

        const timer = window.setTimeout(() => {
            adjustment.adjustmentDetails?.forEach(detail => {
                detail.adjustmentDetailTo?.forEach((row, toRowIndex) => {
                    if (!row.ipNumber && !row.accountNumber) {
                        return;
                    }

                    const rowKey = getToRowKey(detail.id, toRowIndex);
                    const identity = getToRowIdentity(
                        row.ipNumber,
                        row.accountNumber
                    );

                    if (toRowAffiliations[rowKey]?.identity === identity) {
                        return;
                    }

                    resolveToRowAffiliation(
                        detail.id,
                        toRowIndex,
                        row.ipNumber,
                        row.accountNumber
                    );
                });
            });
        }, 300);

        return () => window.clearTimeout(timer);
    }, [adjustment.adjustmentDetails, toRowAffiliations]);

    const commitToRowShareChange = (adjustmentIndex: number, toRowIndex: number, value: string) => {
        const updatedAdjustmentDetails = adjustment.adjustmentDetails.map(
            (detail, detailIndex) => detailIndex === adjustmentIndex
                ? redistributeToRowShares(detail, toRowIndex, value)
                : detail
        );

        updateAdjustmentField(updatedAdjustmentDetails, "adjustmentDetails");
    };

    const onInputChange = (key: string, adjustmentIndex: number, toRowIndex: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = key === 'ipNumber' ? (e.target.value ? Number(e.target.value) : undefined) : e.target.value;

        if (key === 'sharePercentAdjusted') {
            commitToRowShareChange(adjustmentIndex, toRowIndex, value as string);
            return;
        }

        const updatedAdjustmentDetails = adjustment.adjustmentDetails.map(
            (detail, detailIndex) => {
                if (detailIndex !== adjustmentIndex) {
                    return detail;
                }

                return {
                    ...detail,
                    adjustmentDetailTo:
                        detail.adjustmentDetailTo.map(
                            (row, rowIndex) => {
                                if (rowIndex !== toRowIndex) {
                                    return row;
                                }

                                return {
                                    ...row,
                                    [key]: value,
                                    ...(key === "ipNumber"
                                        ? {
                                            accountNumber: "",
                                            name: ""
                                        }
                                        : {})
                                };
                            }
                        )
                };
            }
        );

        updateAdjustmentField(updatedAdjustmentDetails, "adjustmentDetails");
    };

    const showSearchModal = (modalProps: any) => {
        showModal(SEARCH_VIEW, ALLOCATION_ADJUSTMENT_IP_KEY, modalProps, true, "Search");
    };

    const onClickRemove = (direction: string, index: number, detailsIndex?: number) => {
        let updatedDetails = [...adjustment.adjustmentDetails];
        let updatedAllocatedRecords = allocationRecords;

        if (updatedDetails[index]) {
            const detail = updatedDetails[index];
            if (direction === 'From') {
                if (detail.adjustmentDetailFrom.length === 1) {
                    updatedDetails = updatedDetails.filter((_, detailIndex) => detailIndex !== index);
                } else {
                    updatedDetails[index] = {
                        ...detail,
                        adjustmentDetailFrom: detail.adjustmentDetailFrom.filter((_, fromIndex) => fromIndex !== detailsIndex)
                    };
                }
            } else if (direction === 'To') {
                updatedDetails[index] = {
                    ...detail,
                    adjustmentDetailTo: detail.adjustmentDetailTo.filter((_, toIndex) => toIndex !== detailsIndex)
                };
            }
        }
        if (updatedDetails && updatedDetails.length > 0) {
            const adjustmentDetailsIds = updatedDetails.map(item => String(item.id));

            let updatedRecords = updatedAllocatedRecords.map(x => {
                const isSelected = adjustmentDetailsIds.includes(String(x.AllocationUsageHeaderId));
                return { ...x, IsSelected: isSelected };
            });
            updateAllocationRecordField(updatedRecords, 'allocationHeaderDetails')
        } else {
            updatedAllocatedRecords = updatedAllocatedRecords.map(item => ({
                ...item,
                IsSelected: false
            }));
            updateAllocationRecordField(updatedAllocatedRecords, 'allocationHeaderDetails')
        }
        updateAdjustmentField(
            updatedDetails.map(recalculateForFromTotalChange),
            'adjustmentDetails'
        );
    };

    const addRowToAdjustmentDetail = (index) => {
        const { adjustmentDetails } = adjustment;
        const currentDetail = adjustmentDetails[index];
        const workSource = isWorkAdjust
            ? currentDetail.adjustmentDetailTo[0]
            : currentDetail.adjustmentDetailFrom[0];
        const usageGroupTitle =
            currentDetail.adjustmentDetailTo.find(row => row.usageGroupTitle)?.usageGroupTitle
            || currentDetail.adjustmentDetailFrom.find(row => row.usageGroupTitle)?.usageGroupTitle
            || '';
        const updatedDetail = addToRowWithRemainingShare(currentDetail, {
            adjustmentDetailID: currentDetail.adjustmentDetailTo.length,
            adjustmentId: adjustment.id,
            allocationHeaderID: currentDetail.id,
            distribution: adjustment.adjustmentDistribution,
            pool: adjustment.adjustmentPool,
            segment: adjustment.adjustmentSegment,
            workNumber: workSource?.workNumber,
            workName: workSource?.workName,
            usageGroupTitle,
            accountNumber: '',
            name: '',
            role: '',
            ipNumber: undefined,
            sharePercentAdjusted: '',
            pointsAdjusted: '',
            amountAdjusted: ''
        });

        const updatedAdjustmentDetails = adjustmentDetails.map((detail, detailIndex) =>
            detailIndex === index ? updatedDetail : detail
        );
        updateAdjustmentField(updatedAdjustmentDetails, 'adjustmentDetails');
    }

    const applyAdjustmentToAllSelectedAllocationRecords = (adjustmentDetail: IAdjustmentDetails) => {
        let { adjustmentDetails } = adjustment;
        const newAdjustmentDetails: IAdjustmentDetails[] = [];

        const selectedAllocationRecords = allocationRecords?.filter(record =>
            record.IsSelected ||
            adjustmentDetails.some(detail => String(detail.id) === String(record.AllocationUsageHeaderId))
        ) ?? [];

        selectedAllocationRecords.forEach((allocationRecord: AllocationHeaderRowType) => {
            if (isWorkLevelAdjustment || selectedAllocationRecords.length > 0) {
                const existingDetail = adjustmentDetails.find(
                    detail => String(detail.id) === String(allocationRecord.AllocationUsageHeaderId)
                );

                let fromRows = existingDetail?.adjustmentDetailFrom;

                if (!fromRows?.length) {
                    const usageDetails = allocationRecord.UsageDetails.filter((usageRow: any) =>
                        adjustmentDetail.adjustmentDetailFrom.some(templateFrom =>
                            isMatchingAdjustmentDetailFromRow(templateFrom, usageRow)
                        )
                    );

                    if (!usageDetails.length) {
                        return;
                    }

                    fromRows = usageDetails.map((item: any) => ({
                        adjustmentId: adjustment.id,
                        distribution: adjustment.sourceDistribution,
                        segment: adjustment.sourceSegment,
                        allocationHeaderID: item.AllocationUsageHeaderID ?? item[0],
                        ipNumber: item.PayeeIpNameNumber ?? item[1],
                        accountNumber: item.PayeeAccountNumber ?? item[3],
                        name: item.PayeeName ?? item[4],
                        societyCode: item.SocietyCode,
                        amountAdjusted: ((item.Amount ?? item[5]) * 1).toString(),
                        pointsAdjusted: ((item.PointsAllocated ?? item[6]) * 1).toString(),
                        sharePercentAdjusted: ((item.SharePct ?? item[7]) * 1).toString(),
                        pool: adjustmentDetail.adjustmentDetailFrom[0]?.pool,
                        workNumber: allocationRecord.WorkNumber,
                        workName: allocationRecord.WorkName,
                        usageGroupTitle: item.UsageGroupTitle || allocationRecord.UsageGroupTitle,
                        role: item.Role
                    }));
                }

                const newAllocationDetail: IAdjustmentDetails = {
                    id: allocationRecord.AllocationUsageHeaderId,
                    adjustmentDetailFrom: fromRows.map((item) => ({
                        adjustmentId: adjustment.id,
                        distribution: adjustment.sourceDistribution,
                        segment: adjustment.sourceSegment,
                        allocationHeaderID: allocationRecord.AllocationUsageHeaderId,
                        ipNumber: item.ipNumber,
                        societyCode: item.societyCode,
                        accountNumber: item.accountNumber,
                        name: item.name,
                        role: item.role,
                        amountAdjusted: item.amountAdjusted,
                        pointsAdjusted: item.pointsAdjusted,
                        sharePercentAdjusted: item.sharePercentAdjusted,
                        pool: item.pool,
                        workNumber: allocationRecord.WorkNumber,
                        workName: allocationRecord.WorkName,
                        usageGroupTitle: item.usageGroupTitle || allocationRecord.UsageGroupTitle
                    })),
                    adjustmentDetailTo: adjustmentDetail.adjustmentDetailTo.map((obj) => ({ ...obj }))
                }

                newAllocationDetail.adjustmentDetailTo = recalculateForFromTotalChange({
                    ...newAllocationDetail,
                    adjustmentDetailTo: newAllocationDetail.adjustmentDetailTo.map(x => {
                        const adj = newAllocationDetail.adjustmentDetailFrom[0];
                        return {
                            ...x,
                            allocationHeaderID: adj.allocationHeaderID,
                            distribution: adjustment.adjustmentDistribution,
                            segment: adjustment.adjustmentSegment,
                            pool: adjustment.adjustmentPool,
                            workNumber: x.workNumber,
                            workName: x.workName,
                            usageGroupTitle: adj.usageGroupTitle || allocationRecord.UsageGroupTitle
                        };
                    })
                }).adjustmentDetailTo;

                newAdjustmentDetails.push(newAllocationDetail);

            } else {
                adjustmentDetails = [];
                const validateThatAdjustmentDetailIsAlreadyInAllocatedDetails = () => {
                    return adjustmentDetails.some(x => x.id === allocationRecord.AllocationUsageHeaderId);
                }

                const validateThatAdjustmentDetailsAreSimilarEnoughToBeCopied = (comparisonAdjDet: AllocationHeaderRowType, existingAdjDet: IAdjustmentDetails) =>
                    existingAdjDet.adjustmentDetailFrom.every(templateFrom =>
                        comparisonAdjDet.UsageDetails.some(usageRow =>
                            isMatchingAdjustmentDetailFromRow(templateFrom, usageRow)
                        )
                    );

                if (validateThatAdjustmentDetailIsAlreadyInAllocatedDetails()) return;
                if (!validateThatAdjustmentDetailsAreSimilarEnoughToBeCopied(allocationRecord, adjustmentDetail)) return;

                const usageDetails = allocationRecord.UsageDetails.filter((usageRow: any) =>
                    adjustmentDetail.adjustmentDetailFrom.some(templateFrom =>
                        isMatchingAdjustmentDetailFromRow(templateFrom, usageRow)
                    )
                );
                const newAllocationDetail: IAdjustmentDetails = {
                    id: allocationRecord.AllocationUsageHeaderId,
                    adjustmentDetailFrom: usageDetails.map((item: any, index) => ({
                        adjustmentId: adjustment.id,
                        distribution: adjustment.sourceDistribution,
                        segment: adjustment.sourceSegment,
                        allocationHeaderID: item.AllocationUsageHeaderID ?? item[0],
                        ipNumber: item.PayeeIpNameNumber ?? item[1],
                        accountNumber: item.PayeeAccountNumber ?? item[3],
                        name: item.PayeeName ?? item[4],
                        societyCode: item.SocietyCode,
                        amountAdjusted: ((item.Amount ?? item[5]) * 1).toString(),
                        pointsAdjusted: ((item.PointsAllocated ?? item[6]) * 1).toString(),
                        sharePercentAdjusted: ((item.SharePct ?? item[7]) * 1).toString(),
                        pool: adjustmentDetail.adjustmentDetailFrom[0]['pool'],
                        workNumber: allocationRecord.WorkNumber,
                        workName: allocationRecord.WorkName,
                        usageGroupTitle: item.UsageGroupTitle || allocationRecord.UsageGroupTitle
                    })),
                    adjustmentDetailTo: adjustmentDetail.adjustmentDetailTo.map((obj) => ({ ...obj }))
                }

                newAllocationDetail.adjustmentDetailTo = recalculateForFromTotalChange({
                    ...newAllocationDetail,
                    adjustmentDetailTo: newAllocationDetail.adjustmentDetailTo.map(x => {
                        const adj = newAllocationDetail.adjustmentDetailFrom[0];
                        return {
                            ...x,
                            allocationHeaderID: adj.allocationHeaderID,
                            distribution: adjustment.adjustmentDistribution,
                            segment: adjustment.adjustmentSegment,
                            pool: adjustment.adjustmentPool,
                            workNumber: adj.workNumber,
                            workName: adj.workName,
                            usageGroupTitle: adj.usageGroupTitle || allocationRecord.UsageGroupTitle
                        };
                    })
                }).adjustmentDetailTo;

                newAdjustmentDetails.push(newAllocationDetail);

            }
        });

        if (newAdjustmentDetails.length > 0) {
            const updatedIds = new Set(newAdjustmentDetails.map(detail => String(detail.id)));
            const mergedAdjustmentDetails = sortAdjustmentDetailsByAllocationOrder(
                [
                    ...(adjustmentDetails ?? []).filter(detail => !updatedIds.has(String(detail.id))),
                    ...newAdjustmentDetails
                ],
                allocationRecords ?? []
            );
            updateAdjustmentField(mergedAdjustmentDetails, 'adjustmentDetails');
        }
    }
    const searchWorkForAdjustment = (value, index?: number, adjustmentDetail?: IAdjustmentDetails) => {
        const { adjustmentDetails } = adjustment;
        let itemToUpdate = adjustmentDetails.findIndex(item => item.id === adjustmentDetail.id);
        if (itemToUpdate !== -1) {
            adjustmentDetails[itemToUpdate].searchVisible = !Boolean(value);
        }
        updateAdjustmentField(adjustmentDetails, 'adjustmentDetails');
        setWorkAdjustmentIndex(workAdjustmentIndex === index ? null : index);
        setIsWorkLevelAdjustment(true);
    }

    const updateAdjustmentDetails = (updatedAdjustmentDetails: IAdjustmentDetails[], affiliations: IAdjustmentDetailsToSocietyAffiliation[] = []) => {
        if (affiliations.length > 0) {
            setToRowAffiliations(current => {
                const updated = { ...current };

                affiliations.forEach(item => {
                    const rowKey = getToRowKey(
                        item.adjustmentDetailsId,
                        item.toRowIndex
                    );

                    updated[rowKey] = {
                        value: item.societyAffiliation ?? "",
                        loading: false,
                        identity: getToRowIdentity(
                            item.ipiNumber,
                            item.accountNumber
                        )
                    };
                });

                return updated;
            });
        }

        if (updatedAdjustmentDetails) {
            updateAdjustmentField(updatedAdjustmentDetails, "adjustmentDetails");
        }
    };

    const openWork = (openItem: IMatchingWorksRowData) => {
        const isScreenWidthBigEnoughForTwoScreens = window.innerWidth > SCREEN_SIZE_BIG_ENOUGH_FOR_TWO_SCREENS;
        const dataActionList = getDataAction(DataSource.Repertoire, WORK_MAINTENANCE_TOOLBAR);
        const formatFields = SearchRequests.getConfigurationParameter(CONFIGURATION_PARAMETER_FIELD_FORMAT_KEY, WORK_MAINTENANCE_GROUP)
            .then(formats => {
                getWorkDetails(DataSource.Repertoire, openItem.workID, lookupValues, tabs[activeTab].otherIndicatorWorkFlagTypes, tabs[activeTab].readonlyIndicatorWorkFlagTypes, dataActionList, workMaintenanceGeneralDataViewData, formats, undefined, false);
            });
    }


    const calculateTheDataForAnAdjustmentsDetail = (adjustmentDetail: IAdjustmentDetails) => {

        let totalAmount = new Decimal(0);
        let totalPoints = new Decimal(0);
        let totalSharePercentage = new Decimal(0);

        let data: AllocationAdjustmentDetailsRowType[] = [];

        let adjustmentDetailIdOrigin = null;
        if ((adjustmentDetail.adjustmentDetailFrom != null) && (adjustmentDetail.adjustmentDetailFrom.length > 0))
            adjustmentDetailIdOrigin = adjustmentDetail.adjustmentDetailFrom[0];
        if ((adjustmentDetailIdOrigin == null) && (adjustmentDetail.adjustmentDetailTo != null) && (adjustmentDetail.adjustmentDetailTo.length > 0))
            adjustmentDetailIdOrigin = adjustmentDetail.adjustmentDetailTo[0];

        const adjustmentDetailId = adjustmentDetailIdOrigin ? adjustmentDetailIdOrigin.allocationHeaderID : null;
        adjustmentDetail.adjustmentDetailFrom.forEach((adjustmentDetailFrom, i) => {
            data.push({
                key: i === 0 ? `${adjustmentDetailFrom.allocationHeaderID}_999_From` : `${adjustmentDetailFrom.allocationHeaderID}_${(i * -1)}_From`,
                direction: 'From',
                allocationHeaderID: adjustmentDetailFrom.allocationHeaderID,
                distribution: adjustmentDetailFrom.distribution,
                pool: adjustmentDetailFrom.pool,
                segment: adjustmentDetailFrom.segment,
                workNumber: adjustmentDetailFrom.workNumber,
                workName: adjustmentDetailFrom.workName,
                usageGroupTitle: adjustmentDetailFrom.usageGroupTitle,
                accountNumber: adjustmentDetailFrom.accountNumber,
                name: adjustmentDetailFrom.name,
                role: adjustmentDetailFrom.role,
                ipiNumber: adjustmentDetailFrom.ipNumber,
                societyAffiliation:
                    getSocietyAffiliation(
                        adjustmentDetailFrom.societyCode
                    ),
                sharePctAdj: adjustmentDetailFrom.sharePercentAdjusted,
                pointsAdj: adjustmentDetailFrom.pointsAdjusted,
                amountAdj: adjustmentDetailFrom.amountAdjusted,
                adjustmentDetailID: i
            });
            totalAmount = totalAmount.plus(toDecimal(adjustmentDetailFrom.amountAdjusted));
            totalPoints = totalPoints.plus(toDecimal(adjustmentDetailFrom.pointsAdjusted));
            totalSharePercentage = totalSharePercentage.plus(toDecimal(adjustmentDetailFrom.sharePercentAdjusted));
        });

        const pointsList: Decimal[] = [];
        const amountList: Decimal[] = [];
        const rowRefs: AllocationAdjustmentDetailsRowType[] = [];
        let pointsSum = new Decimal(0);
        
        const allToSharesBlank = adjustmentDetail.adjustmentDetailTo.every(row => {
            const share = row.sharePercentAdjusted;
            return share === undefined || share === null || String(share).trim() === '';
        });

        const displayDetail = allToSharesBlank
            ? syncAdjustmentDetailDerivedValues(adjustmentDetail)
            : adjustmentDetail;

        displayDetail.adjustmentDetailTo.forEach((adjustmentDetailTo, i) => {
            const effectiveShare = toDecimal(adjustmentDetailTo.sharePercentAdjusted);
            const isValidShare = effectiveShare.greaterThan(0) && !totalSharePercentage.isZero();
            const pointsValue = isValidShare
                ? toDecimal(adjustmentDetailTo.pointsAdjusted)
                : new Decimal(0);
            const amountValue = isValidShare
                ? toDecimal(adjustmentDetailTo.amountAdjusted)
                : new Decimal(0);

            pointsList.push(pointsValue);
            amountList.push(amountValue);
            pointsSum = pointsSum.plus(pointsValue);

            const affiliationKey = getToRowKey(adjustmentDetailId, i);
            const rowIdentity = getToRowIdentity(adjustmentDetailTo.ipNumber, adjustmentDetailTo.accountNumber);
            const affiliationState = toRowAffiliations[affiliationKey];
            const isCurrentAffiliation = affiliationState?.identity === rowIdentity;

            const row: AllocationAdjustmentDetailsRowType = {
                key: i === 0 ? `${adjustmentDetailTo.allocationHeaderID}_999_To` : `${adjustmentDetailTo.allocationHeaderID}_${(i * -1)}_To`,
                direction: 'To',
                allocationHeaderID: adjustmentDetailTo.allocationHeaderID,
                distribution: adjustmentDetailTo.distribution,
                pool: adjustmentDetailTo.pool ? adjustmentDetailTo.pool : adjustmentDetail.adjustmentDetailFrom[0].pool,
                segment: adjustmentDetailTo.segment,
                workNumber: adjustmentDetailTo.workNumber,
                workName: adjustmentDetailTo.workName,
                usageGroupTitle: adjustmentDetailTo.usageGroupTitle,
                accountNumber: adjustmentDetailTo.accountNumber,
                name: adjustmentDetailTo.name,
                role: adjustmentDetailTo.role,
                ipiNumber: adjustmentDetailTo.ipNumber,
                societyAffiliation: isCurrentAffiliation ? affiliationState.value : "",
                affiliationKey,
                sharePctAdj: formatDecimal(effectiveShare),
                pointsAdj: formatDecimal(pointsValue),
                amountAdj: formatDecimal(amountValue),
                adjustmentDetailID: i,
                toRowIndex: i
            };

            data.push(row);
            rowRefs[i] = row;
            adjustmentDetailTo.adjustmentDetailID = i;
        });

        return data;
    }


        let filteredSearchViewData = { fields: [] };
        if (searchViewData) {
            filteredSearchViewData = { fields: searchViewData.fields.filter(f => !['usageType', 'searchType'].includes(f.name)) };
        }
        let tableList = [];

        if (!loaded || initialAffiliationsLoading) {
            return (
                <div className="loaderWrapper">
                    <div className="spinner-dualball">
                        <div />
                        <div />
                        <div />
                    </div>
                </div>
            );
        }

        if (loaded) {
            if (adjustment?.adjustmentDetails && adjustment?.adjustmentDetails.length > 0) {
                adjustment.adjustmentDetails.forEach((adjustmentDetail, index) => {

                    const data = calculateTheDataForAnAdjustmentsDetail(adjustmentDetail);

                    let columns = [
                        {
                            title: '', dataIndex: 'direction', key: 'direction', render: (_, value, index) => {
                                const text = value.direction;
                                return <><Space size="middle"><strong>{text}</strong></Space></>
                            },
                        },
                        { title: viewData[ALLOCATION_HEADER_ID], dataIndex: ALLOCATION_HEADER_ID, key: ALLOCATION_HEADER_ID },
                        { title: viewData[ALLOCATION_DISTRIBUTION], dataIndex: ALLOCATION_DISTRIBUTION, key: ALLOCATION_DISTRIBUTION },
                        { title: viewData[ALLOCATION_POOL], dataIndex: ALLOCATION_POOL, key: ALLOCATION_POOL },
                        { title: viewData[ALLOCATION_SEGMENT], dataIndex: ALLOCATION_SEGMENT, key: ALLOCATION_SEGMENT },
                        {
                            title: viewData[ALLOCATION_WORK_NUMBER], dataIndex: ALLOCATION_WORK_NUMBER, key: ALLOCATION_WORK_NUMBER, render: (text, record, index) => {
                                return <><div className={"widerFieldNumber"}><span>{record.workNumber}</span></div></>
                            }
                        },
                        {
                            title: viewData[ALLOCATION_WORK_NAME], dataIndex: ALLOCATION_WORK_NAME, key: ALLOCATION_WORK_NAME, render: (text, record, index) => {
                                return <><div className={"widerField"}><span>{record.workName}</span></div></>
                            }
                        },
                        {
                            title: viewData[ALLOCATION_USAGE_GROUP_TITLE], dataIndex: ALLOCATION_USAGE_GROUP_TITLE, key: ALLOCATION_USAGE_GROUP_TITLE, render: (text, record, index) => {
                                return <><div className={"widerField"}><span>{record.usageGroupTitle}</span></div></>
                            }
                        },
                        {
                            title: viewData[ALLOCATION_ACCOUNT_NUMBER], dataIndex: ALLOCATION_ACCOUNT_NUMBER, key: ALLOCATION_ACCOUNT_NUMBER, render: (text, record, index) => {
                                return <Input title={viewData[ALLOCATION_ACCOUNT_NUMBER]} className={"widerFieldNumber"} value={record.accountNumber} readOnly={true} disabled={true} onChange={onInputChange("accountNumber", index, record.adjustmentDetailID)} />
                            }
                        },
                        {
                            title: viewData[ALLOCATION_IP_NAME], dataIndex: ALLOCATION_IP_NAME, key: ALLOCATION_IP_NAME, render: (text, record, index) => {
                                return <Input title={viewData[ALLOCATION_IP_NAME]} className={"widerField"} value={record.name} readOnly={true} disabled={true} onChange={onInputChange("name", index, record.adjustmentDetailID)} />
                            }
                        },
                        {
                            title: viewData[ALLOCATION_CONTRIBUTOR_ROLE], dataIndex: ALLOCATION_CONTRIBUTOR_ROLE, key: ALLOCATION_CONTRIBUTOR_ROLE, render: (text, record) => {
                                return <Input title={viewData[ALLOCATION_CONTRIBUTOR_ROLE]} className={"percentageField"} value={record.role} readOnly={record.direction === 'From' ? true : isReadonly} type={'text'} onChange={onInputChange("role", index, record.adjustmentDetailID)} disabled={record.direction === 'From' ? true : isReadonly} />
                            }
                        },
                        {
                            title: viewData[ALLOCATION_IPI_NUMBER], dataIndex: ALLOCATION_IPI_NUMBER, key: ALLOCATION_IPI_NUMBER,                             render: (text, record) => (
                                <InputWithKebabMenu
                                    variant="table"
                                    className="hasIcon"
                                    kebab={(record.direction === 'To' && !isReadonly) ? (
                                        <ShowMore
                                            options={[
                                                {
                                                    text: 'Select IP',
                                                    onClick: () => { showSearchModal({ adjustmentDetails: adjustment.adjustmentDetails[index], index: record.adjustmentDetailID }); },
                                                    icon: EXTERNAL_SVG
                                                }
                                            ]}
                                        />
                                    ) : null}
                                >
                                    <Input className={"widerFieldNumber"} value={record.ipiNumber} readOnly={record.direction === 'From' ? true : isReadonly} type={'number'} disabled={record.direction === 'From' ? true : isReadonly} onChange={onInputChange("ipNumber", index, record.adjustmentDetailID)} />
                                </InputWithKebabMenu>
                            )
                        },
                        {
                            title: viewData[ALLOCATION_SOCIETY_AFFILIATION] ?? "Society Affiliation",
                            dataIndex: ALLOCATION_SOCIETY_AFFILIATION,
                            key: ALLOCATION_SOCIETY_AFFILIATION,
                            render: (text, record) => {
                                const affiliationState = record.affiliationKey ? toRowAffiliations[record.affiliationKey] : undefined;

                                return (
                                    <div className="hasIcon">
                                        <span>{record.societyAffiliation}</span>
                                        {affiliationState?.loading && (
                                            <Spin size="small" />
                                        )}
                                    </div>
                                );
                            }
                        },
                        {
                            title: viewData[ALLOCATION_SHARE_ADJUSTED], dataIndex: ALLOCATION_SHARE_ADJUSTED, key: ALLOCATION_SHARE_ADJUSTED, render: (text, record) => {
                                const isEditableShare = record.direction === 'To' && record.toRowIndex !== undefined && !isReadonly;
                                const shareEditKey = isEditableShare ? getShareEditKey(index, record.toRowIndex) : undefined;
                                const isEditingShare = shareEditKey !== undefined && shareEditBuffer[shareEditKey] !== undefined;
                                const displayValue = isEditingShare ? shareEditBuffer[shareEditKey] : record.sharePctAdj;

                                return (
                                    <Input
                                        title={viewData[ALLOCATION_SHARE_ADJUSTED]}
                                        className={"adjustedValueField"}
                                        value={displayValue}
                                        readOnly={record.direction === 'From' ? true : isReadonly}
                                        disabled={record.direction === 'From' ? true : isReadonly}
                                        type={'number'}
                                        onFocus={isEditableShare ? (e: React.FocusEvent<HTMLInputElement>) => {
                                            setShareEditBuffer(current => ({ ...current, [shareEditKey]: record.sharePctAdj }));
                                            // Selecting the whole value lets users just type over it instead of
                                            // deleting the long decimal one character at a time.
                                            e.target.select();
                                        } : undefined}
                                        onChange={isEditableShare ? (e: React.ChangeEvent<HTMLInputElement>) => {
                                            const value = e.target.value;
                                            // Keep displaying exactly what was typed in this field (so the cursor
                                            // doesn't jump), while still committing so sibling TO rows update live.
                                            setShareEditBuffer(current => ({ ...current, [shareEditKey]: value }));
                                            commitToRowShareChange(index, record.toRowIndex, value);
                                        } : undefined}
                                        onBlur={isEditableShare ? (e: React.FocusEvent<HTMLInputElement>) => {
                                            const value = e.target.value;
                                            setShareEditBuffer(current => {
                                                const updated = { ...current };
                                                delete updated[shareEditKey];
                                                return updated;
                                            });
                                            commitToRowShareChange(index, record.toRowIndex, value);
                                        } : undefined}
                                        onKeyDown={isEditableShare ? (e: React.KeyboardEvent<HTMLInputElement>) => {
                                            if (e.key === 'Enter') {
                                                (e.target as HTMLInputElement).blur();
                                            }
                                        } : undefined}
                                    />
                                );
                            }
                        },
                        {
                            title: viewData[ALLOCATION_POINT_ADJUSTED], dataIndex: ALLOCATION_POINT_ADJUSTED, key: ALLOCATION_POINT_ADJUSTED, render: (text, record) => (
                                <Input title={viewData[ALLOCATION_POINT_ADJUSTED]} className={"adjustedValueField"} value={record.pointsAdj} readOnly={true} disabled={true} type={'number'} />
                            )
                        }, {
                            title: viewData[ALLOCATION_AMOUNT_ADJUSTED], dataIndex: ALLOCATION_AMOUNT_ADJUSTED, key: ALLOCATION_AMOUNT_ADJUSTED, render: (text, record) => (
                                <Input title={viewData[ALLOCATION_AMOUNT_ADJUSTED]} className={"adjustedValueField"} value={record.amountAdj} readOnly={true} disabled={true} type={'number'} />
                            )
                        },
                        {
                            title: '', dataIndex: REMOVE_ACTION, key: REMOVE_ACTION, render: (text, record) => (
                                record.direction === 'From' ?
                                    <div className="tableCell">
                                        <IconTextButton onClick={() => onClickRemove('From', index, record.adjustmentDetailID)} text={''} icon={Delete20Regular} disabled={isReadonly} />
                                    </div>
                                    :
                                    <div>
                                        <IconTextButton onClick={() => onClickRemove('To', index, record.adjustmentDetailID)} text={''} icon={Delete20Regular} disabled={isReadonly} />
                                    </div>
                            )
                        }
                    ]
                    tableList.push(
                        <div key={index}>
                            <div className="row">
                                <div className="col-md-6">
                                    {index === 0 &&
                                        <span className="title">Adjustment Details for Selected Allocation Records</span>
                                    }

                                </div>
                                {isAllocationSelected && <div className="col-md-3">
                                    <ActionButton
                                        buttonAction={() => searchWorkForAdjustment(adjustmentDetail.searchVisible, index, adjustmentDetail)}
                                        buttonText={(adjustmentDetail.searchVisible) ? 'Close Search Work for Adjustment' : 'Search Work for Adjustment'}
                                        disabled={isReadonly}
                                    />
                                </div>}
                                <div className="col-md-3" style={{ float: 'inline-end' }}>
                                    <ActionButton
                                        buttonAction={() => applyAdjustmentToAllSelectedAllocationRecords(adjustmentDetail)}
                                        buttonText={'Apply Adjustment to All Selected Allocation Records'}
                                        disabled={isReadonly}
                                    />
                                </div>
                            </div>
                            {(adjustmentDetail.searchVisible) && <div className="row" style={{ padding: '12px' }}>
                                <UsageSearchView
                                    searchViewData={filteredSearchViewData}
                                    searchResultsTableData={searchResultsTableData}
                                    componentInstance={USAGE_MATCHING_WORKS_SECTION_KEY}
                                    modalProps={undefined}
                                    searchUsages={searchUsages}
                                    searchSuccessful={searchSuccessful}
                                    indexOfFirstResult={indexOfFirstResult}
                                    indexOfLastResult={indexOfLastResult}
                                    resultsPerPage={resultsPerPage}
                                    currentPage={currentPage}
                                    resetPagination={resetPagination}
                                    updatePagination={updatePagination}
                                    hideModal={hideModal}
                                    searchMatchWorks={searchMatchWorks}
                                    searchMatchProducts={searchMatchProducts}
                                    matchWorkSearchResult={adjustmentDetail.workSearchResult}
                                    getUsageDetails={undefined}
                                    expandMatchWorkResult={expandMatchWorkResult}
                                    expandedMatchWorkResults={expandedMatchWorkResults}
                                    addMatchworkToUsage={undefined}
                                    searchDataChanged={searchDataChanged}
                                    lookupValues={lookupValues}
                                    lookupSources={lookupSources}
                                    distributionTypes={distributionTypes}
                                    usageMatchingDefaultsWorks={usageMatchingDefaultsWorks}
                                    usageExpandAllResults={usageExpandAllResults}
                                    updateAdjustmentDetail={updateAdjustmentDetails}
                                    getWorkDetails={openWork.bind(this)}
                                    adjustmentDetails={adjustment.adjustmentDetails}
                                    adjustment={adjustment}
                                    workAdjustmentIndex={index}
                                    updateAdjustmentField={updateAdjustmentField}
                                    enabledAdjustmentICEWorkSearch={enabledAdjustmentICEWorkSearch}
                                    showModal={showModal}
                                    allocationRecords={allocationRecords}
                                />
                            </div>}
                            <div className='tableContainer'>
                                <Table
                                    rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
                                    columns={columns}
                                    dataSource={data}
                                    pagination={false}
                                />
                                <div className='row'>
                                    <SizedActionButton buttonAction={() => addRowToAdjustmentDetail(index)} buttonText={'Add New'} disabled={isReadonly} />
                                </div>
                            </div>
                        </div>
                    )
                })
            }
            return <div>{tableList}</div>
        }
        else return (<div></div>)
};

export default AllocationAdjustmentDetailsView;
